// Step for entering email address (for one-time passcode)

import React from 'react';
import { AuthStepCard } from './AuthStepCard';
import { Button } from '@/components/ui/button';
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Field, FieldError, FieldGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { analyticsService } from '../../services/analyticsService';
import { requestOTP } from '../../transport/api/publicApi';
import {
  InputValidationError,
  useValidatedInput,
} from '../../hooks/useValidatedInput';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('EmailStep');

interface EmailStepProps {
  onSubmit: (email: string) => void;
  onBack: () => void;
}

const EmailStep: React.FC<EmailStepProps> = ({ onSubmit, onBack }) => {
  const email = useValidatedInput({
    rules: ['email'],
    transforms: ['lowercase'],
    onSubmit: async (normalizedEmail) => {
      try {
        await requestOTP(normalizedEmail);
        logger.info('Submitted request for passcode');
        analyticsService.trackFormSubmit('otp_request', 'email');
        onSubmit(normalizedEmail);
      } catch (error) {
        logger.error('Error sending OTP:', error);
        throw new InputValidationError('Error sending OTP. Please try again.');
      }
    },
  });

  return (
    <AuthStepCard>
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">Email Login</CardTitle>
        <CardDescription>One-time passcode will be emailed</CardDescription>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <Field data-invalid={!!email.error}>
            <Input
              id="login-email"
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoComplete="email"
              placeholder="Enter your email address"
              {...email.inputProps}
              aria-invalid={!!email.error}
              className="h-11 text-base"
            />
            {email.error ? <FieldError>{email.error}</FieldError> : null}
          </Field>
          <Button
            type="button"
            onClick={() => void email.submit()}
            disabled={email.isSubmitting}
            className="w-full cursor-pointer"
            size="xl"
          >
            {email.isSubmitting ? 'Sending...' : 'Submit'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onBack}
            className="w-full cursor-pointer"
            disabled={email.isSubmitting}
            size="xl"
          >
            Change login method
          </Button>
        </FieldGroup>
      </CardContent>
    </AuthStepCard>
  );
};

export default EmailStep;
